import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { gql } from "@apollo/client";
import PrecautionOctagon from "~/components/patient/PrecautionOctagon";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useHomeHealthPatientNotes } from "~/features/home-health-demo";
import { partition, reverse, sortBy, startCase, uniqBy } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import PatientIdentifiers from "~/components/common/PatientIdentifiers";
import PermissionOrHide from "~/components/common/PermissionOrHide";
import { Permission, useHasPermission, useOrganizationalUnitSegmentationEnabled } from "~/hooks/permissions";
import EditPatient from "~/components/patient/EditPatient";
import { datef, formatShortDate, getFriendlyTimezoneName } from "@caredna-marlin/utils/dates";
import { ServiceLineScheduleConfig } from "@caredna-marlin/utils/configurationTypes";
import {
  useCreateEpisodeMutation,
  useEpisodeOrganizationalUnitsQuery,
  useUpdateEpisodeMutation,
} from "~/graphql/__generated__/graphql";
import { useOrgConfig } from "~/providers/OrgConfigProvider";
import { formatPhoneNumber } from '~/utils/format';
import { isHospitalAtHomeServiceLine } from "~/utils/intakeServiceLine";
import ExpandableText from "~/components/common/ExpandableText";
import { Patient } from '~/utils/forms';
import CareEpisodeDialog from '../patient/EpisodeDialog';
import EpisodePrecautionsDialog from '../patient/EpisodePrecautionsDialog';
import { useSelectableServiceLineIds } from "~/hooks/useDefaultServiceLines";
import LabeledSelect from "~/components/common/forms/LabeledSelect";
import { useSnackbar } from "~/hooks/useSnackbar";

type Props = {
  patient: Patient | undefined | null;
  loading: boolean;
};

const PATIENT_EPISODE_REFETCH_QUERIES = [
  "PatientDetail",
  "PatientDetailScreen",
];

export default function SchedulerPatientBrief({ patient, loading }: Props) {
  const homeHealthNotes = useHomeHealthPatientNotes(patient?.patientNotes || "", patient ?? undefined);
  const [open, setOpen] = useState<null | HTMLElement | "editCareReqs">(null);
  const [editing, setEditing] = useState<{ episodeId: number | undefined, serviceLineId: number, reopen?: boolean, defaultTemplateName?: string, defaultAdmitAfterMs?: number } | null>(null);
  const [editingPatient, setEditingPatient] = useState(false);
  const [managingPrecautionsFor, setManagingPrecautionsFor] = useState<{ episodeId: number, serviceLineId: number } | null>(null);
  const [pendingEpisodeAssignment, setPendingEpisodeAssignment] = useState<{ serviceLineId: number, episodeId?: number } | null>(null);
  const [selectedOrganizationalUnitId, setSelectedOrganizationalUnitId] = useState<number | null>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const { setErrorMessage } = useSnackbar();

  // After modifying an episode, take the user to the patient page. The dialog
  // can be launched from screens other than the patient page (e.g. the
  // scheduler patient view at /scheduler/patient/:id), and users expect to land
  // on the patient afterwards. When already on the patient page this is a no-op
  // so we skip navigating.
  const navigateToPatientIfNeeded = () => {
    if (!patient) { return; }
    const patientPath = `/patients/${patient.id}`;
    if (location.pathname !== patientPath) {
      navigate(patientPath);
    }
  };

  const { serviceLines, precautions } = useOrgConfig();
  const hasManagePatientsPermission = useHasPermission(Permission.managePatients);
  const organizationalUnitSegmentationEnabled = useOrganizationalUnitSegmentationEnabled();
  const {
    data: organizationalUnitsData,
    loading: loadingOrganizationalUnits,
    error: organizationalUnitsError,
  } = useEpisodeOrganizationalUnitsQuery({
    skip: !hasManagePatientsPermission,
  });
  const organizationalUnits = organizationalUnitsData?.organizationalUnit ?? [];
  const activeOrganizationalUnits = organizationalUnits.filter((organizationalUnit) => organizationalUnit.active);
  const hasActivePrecautions = Object.values(precautions).some((precaution) => precaution?.active);
  const serviceLineSchedulingConfigById = useMemo(() => {
    const configs: Record<number, ServiceLineScheduleConfig> = {};
    Object.values(serviceLines).forEach((serviceLine) => {
      if (!serviceLine) { return; }
      configs[serviceLine.id] = serviceLine.schedulingConfig;
    });
    return configs;
  }, [serviceLines]);

  const [createEpisode, { loading: loadingCreate }] = useCreateEpisodeMutation({
    awaitRefetchQueries: true,
    refetchQueries: PATIENT_EPISODE_REFETCH_QUERIES,
  });
  const [updateEpisode, { loading: loadingUpdate }] = useUpdateEpisodeMutation({
    awaitRefetchQueries: true,
    refetchQueries: PATIENT_EPISODE_REFETCH_QUERIES,
  });
  const savingEpisodeAssignment = pendingEpisodeAssignment?.episodeId == null
    ? loadingCreate
    : loadingUpdate;

  const setEpisodeActive = async (episodeId: number, active: boolean) => {
    setOpen(null);
    await updateEpisode({
      variables: {
        episodeId,
        organizationalUnitId: null,
        active,
        autoschedulerGroup: null,
        careRequirements: null,
        precautionIds: null,
      },
    });
  };

  const createDefaultEpisode = async (serviceLineId: number, organizationalUnitId: number | null, closeBeforeCreate = false) => {
    if (!patient) { return false; }
    if (closeBeforeCreate) {
      setOpen(null);
    }

    try {
      await createEpisode({
        variables: {
          patientId: patient.id,
          serviceLineId,
          organizationalUnitId,
          autoschedulerGroup: null,
          careRequirements: null,
          precautionIds: null,
        },
      });

      setOpen(null);
      setEditing(null);
      return true;
    } catch (e) {
      console.error(e);
      setErrorMessage("Couldn't create the episode. Please try again.");
      return false;
    }
  };

  const newEpisode = async (serviceLineId: number) => {
    if (!patient) { return; }
    if (organizationalUnitsError) { return; }
    if (!serviceLineSchedulingConfigById[serviceLineId]?.useAutoscheduler) {
      if (activeOrganizationalUnits.length <= 1) {
        await createDefaultEpisode(serviceLineId, activeOrganizationalUnits[0]?.id ?? null, true);
      } else {
        setOpen(null);
        setPendingEpisodeAssignment({ serviceLineId });
        setSelectedOrganizationalUnitId(null);
      }
      return;
    }
    setEditing({ episodeId: undefined, serviceLineId });
    setOpen("editCareReqs");
  };

  // eslint-disable-next-line prefer-const
  let [activeEpisodes, inactiveEpisodes] = patient ? partition(reverse(sortBy(patient.episodes, "updatedAt")), "active") : [[], []];

  // Only display the most recent inacitve episode for a given service line,
  // and only display it if there isn't an active episode for that same service line.
  inactiveEpisodes = inactiveEpisodes?.filter(ep => !activeEpisodes.find(aep => aep.serviceLineId === ep.serviceLineId));
  inactiveEpisodes = uniqBy(inactiveEpisodes, "serviceLineId");

  const selectableServiceLines = useSelectableServiceLineIds()
  const episodes = !patient ? [] : [...activeEpisodes, ...inactiveEpisodes];

  // Only display menu items to add an episode for service lines where we don't already have an
  // open one
  const potentialServiceLines = Object.values(serviceLines)
    .filter(sl => sl != undefined)
    .filter(sl => !episodes.find(ep => ep.active && ep.serviceLineId === sl.id))
    .filter(sl => selectableServiceLines.includes(sl.id)) || [];

  const hasEpisodeActions = episodes.length > 0 || potentialServiceLines.length > 0;

  // When the patient detail page is opened from the Intake Queue with a
  // requested service line, auto-trigger the clinical constraints dialog
  // once the patient and service line config are loaded.
  const autoEpisodeFiredRef = useRef(false);
  const navState = location.state as { startNewEpisode?: string; serviceLineId?: number; templateName?: string; equipmentEtaMs?: number } | null;
  const requestedNewEpisode = navState?.startNewEpisode;
  const requestedServiceLineId = navState?.serviceLineId;
  const requestedTemplateName = navState?.templateName;
  const requestedAdmitAfterMs = navState?.equipmentEtaMs;
  useEffect(() => {
    if (autoEpisodeFiredRef.current) return;
    if (!patient || !requestedNewEpisode) return;
    if (potentialServiceLines.length === 0) return;

    const wantsHcah = requestedNewEpisode === "hcah";
    const matchesHcah = (sl: { name: string }) => isHospitalAtHomeServiceLine(sl.name);

    // Prefer an explicitly requested service line (Coordinate intake resolves it
    // from the selected provider); otherwise pick the best-matching Hospital at Home line,
    // falling back to the first selectable one so the demo flow always shows the
    // dialog.
    const explicit = requestedServiceLineId
      ? potentialServiceLines.find((sl) => sl.id === requestedServiceLineId)
      : undefined;
    const target =
      explicit ??
      (wantsHcah
        ? potentialServiceLines.find(matchesHcah) ?? potentialServiceLines[0]
        : potentialServiceLines[0]);
    if (!target) return;

    autoEpisodeFiredRef.current = true;
    // Clear the location state so a refresh doesn't re-trigger the flow.
    navigate(location.pathname, { replace: true, state: null });

    // When the target service line uses the autoscheduler, open the clinical
    // constraints dialog directly (bypassing the useAutoscheduler shortcut in
    // newEpisode) so the user lands in the care-requirement editor for this
    // intake flow. When it does not, the autoscheduler dialog doesn't apply:
    // create a default episode and route into the standard manual scheduling
    // flow instead, mirroring newEpisode's non-autoscheduler behavior.
    if (!serviceLineSchedulingConfigById[target.id]?.useAutoscheduler) {
      const patientId = patient.id;
      const targetServiceLineId = target.id;
      if (activeOrganizationalUnits.length === 1) {
        void createDefaultEpisode(targetServiceLineId, activeOrganizationalUnits[0].id, true).then(() => {
          // Seed the scheduling parameters popup with the target service line via
          // the `serviceLine` query param (read in SchedulerScreen).
          navigate(`/scheduler/patient/${patientId}?serviceLine=${targetServiceLineId}`);
        });
      } else {
        setOpen(null);
        setPendingEpisodeAssignment({ serviceLineId: targetServiceLineId });
        setSelectedOrganizationalUnitId(null);
      }
      return;
    }
    setEditing({ episodeId: undefined, serviceLineId: target.id, defaultTemplateName: requestedTemplateName, defaultAdmitAfterMs: requestedAdmitAfterMs });
    setOpen("editCareReqs");
  }, [patient, requestedNewEpisode, requestedServiceLineId, requestedTemplateName, requestedAdmitAfterMs, potentialServiceLines, location.pathname, navigate]);

  const episodeBtnTooltip = hasManagePatientsPermission ? "Click to manage" : "";

  // TODO: Change button style
  return (
    <Card sx={{ width: 1, minWidth: "100%" }}>
      <CardContent>
        <Stack
          direction="row"
          spacing={2}
          justifyContent="space-between"
          minHeight={32}
          alignItems="center"
        >
          {loading ? (
            <Skeleton variant="text" sx={{ fontSize: "1.2rem" }} width={240} />
          ) : (
            <Typography gutterBottom variant="h5" component="div">
              {patient?.firstName} {patient?.lastName}
              <Tooltip title={episodeBtnTooltip} >
                <Button
                  sx={{ml: 2}}
                  onClick={(e) => {
                    if (hasManagePatientsPermission && hasEpisodeActions) {
                      setOpen(e.currentTarget);
                    }
                  }}
                  disabled={loadingCreate || loadingUpdate}
                  loading={loadingCreate || loadingUpdate}
                  variant={patient?.episodes.find(e => e.active) ? "contained" : "outlined"}
                >
                  {patient?.episodes.find(e => e.active) ? "Active" : patient?.episodes.length == 0 ? "Inactive" : "Former"}
                </Button>
              </Tooltip>
              <Menu open={open instanceof HTMLElement} onClose={() => setOpen(null)} anchorEl={open instanceof HTMLElement ? open : null}>
                {episodes.map(ep => {
                  const useAutoscheduler = serviceLineSchedulingConfigById[ep.serviceLineId]?.useAutoscheduler;
                  const disabled = !selectableServiceLines.includes(ep.serviceLineId)
                  const canEditEpisode = ep.active && !disabled;
                  return (
                    <MenuItem
                      key={ep.id}
                      onClick={() => {
                        if (!ep.active && useAutoscheduler) {
                          setOpen("editCareReqs");
                          setEditing({ episodeId: ep.id, serviceLineId: ep.serviceLineId, reopen: true });
                        } else {
                          setEpisodeActive(ep.id, !ep.active);
                        }
                      }}
                      sx={{ display: "flex", alignItems: "center", gap: 0.5, pr: 0.5 }}
                      disabled={disabled}
                    >
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        {!disabled && (ep.active ? "Close" : "Reopen")} {serviceLines[ep.serviceLineId]?.name || ""} Episode{" "}
                        ({ep.active ? "opened" : "closed"} {datef(ep.updatedAt, "MMM d, yyyy")})
                      </Box>
                      {canEditEpisode && (
                        <Tooltip title="Edit episode">
                          <IconButton
                            aria-label="Edit episode"
                            sx={{ alignSelf: "stretch", borderRadius: 1 }}
                            disabled={loadingOrganizationalUnits || !!organizationalUnitsError}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (useAutoscheduler) {
                                setOpen("editCareReqs");
                                setEditing({ episodeId: ep.id, serviceLineId: ep.serviceLineId });
                              } else {
                                setOpen(null);
                                setSelectedOrganizationalUnitId(ep.organizationalUnitId);
                                setPendingEpisodeAssignment({ episodeId: ep.id, serviceLineId: ep.serviceLineId });
                              }
                            }}
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {ep.active && !disabled && hasActivePrecautions && (
                        <Tooltip title="Manage precautions">
                          <IconButton
                            aria-label="Manage precautions"
                            sx={{ alignSelf: "stretch", borderRadius: 1 }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpen(null);
                              setManagingPrecautionsFor({ episodeId: ep.id, serviceLineId: ep.serviceLineId });
                            }}
                          >
                            <PrecautionOctagon color="currentColor" size={20} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </MenuItem>
                  );
                })}
                {episodes.length > 0 && potentialServiceLines.length > 0 && <Divider />}
                {potentialServiceLines.map(sl => <MenuItem
                  key={sl.id}
                  disabled={loadingOrganizationalUnits || !!organizationalUnitsError}
                  onClick={() => newEpisode(sl.id)}
                >
                  New {sl.name} Episode
                </MenuItem>)}
              </Menu>
            </Typography>
          )}
          <Typography gutterBottom component="div">
            {loading ? (
              <Skeleton
                variant="text"
                sx={{ fontSize: "0.8rem" }}
                width={420}
              />
            ) : (
              <Typography component="div">
                {formatPhoneNumber(patient?.phone, 'Phone unknown')}
                {bull}
                {patient?.dateOfBirth ? formatShortDate(patient.dateOfBirth) : "DOB unknown"}
                {bull}
                {startCase(patient?.sex || "Sex unknown")}
                {bull}
                {patient?.addressStreet}, {patient?.addressCity}{" "}
                {patient?.addressState} {patient?.addressZip}
                {bull}
                {patient?.addressTimezone && getFriendlyTimezoneName(patient?.addressTimezone)}
                <PatientIdentifiers patient={patient} />

                <PermissionOrHide permission={Permission.managePatients}>
                  <Button onClick={() => setEditingPatient(true)}>Edit</Button>
                </PermissionOrHide>
              </Typography>
            )}
          </Typography>
        </Stack>

        {organizationalUnitSegmentationEnabled === true && !loadingOrganizationalUnits && !organizationalUnitsError && hasManagePatientsPermission && activeOrganizationalUnits.length === 0 && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            No active organizational units are available.
          </Alert>
        )}

        <Divider sx={{ mt: 1, mb: 1.2 }} />

        <Box minHeight="20px">
          {loading ? (
            <>
              <Skeleton
                variant="text"
                sx={{ fontSize: "0.5em", mb: 0.5 }}
              />
              <Skeleton
                variant="text"
                sx={{ fontSize: "0.5em" }}
                width={420}
              />
            </>
          ) : (
            <ExpandableText
              maxLines={3}
              text={homeHealthNotes}
              emptyText="No patient notes provided"
            />
          )}
        </Box>
      </CardContent>

      {editingPatient && (
        <EditPatient
          patient={patient || undefined}
          onClose={() => setEditingPatient(false)}
        />
      )}
      {editing && open === "editCareReqs" && patient && (
        <CareEpisodeDialog
          key={`${editing.episodeId}-${editing.serviceLineId}`}
          episodeId={editing.episodeId}
          patient={patient}
          serviceLineId={editing.serviceLineId}
          organizationalUnits={organizationalUnits}
          reopen={editing.reopen}
          defaultTemplateName={editing.defaultTemplateName}
          defaultAdmitAfterMs={editing.defaultAdmitAfterMs}
          onSkip={editing.episodeId === undefined
            ? async (organizationalUnitId) => { await createDefaultEpisode(editing.serviceLineId, organizationalUnitId); }
            : undefined}
          onSaved={() => {
            setOpen(null);
            setEditing(null);
            navigateToPatientIfNeeded();
          }}
          onClose={() => {
            setOpen(null);
            setEditing(null);
          }}
        />
      )}
      {pendingEpisodeAssignment != null && (
        <Dialog
          open
          onClose={() => {
            if (!savingEpisodeAssignment) { setPendingEpisodeAssignment(null); }
          }}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle>{pendingEpisodeAssignment.episodeId == null ? "Select organizational unit" : "Change organizational unit"}</DialogTitle>
          <DialogContent dividers>
            <LabeledSelect
              label="Organizational unit"
              fullWidth
              size="small"
              disabled={savingEpisodeAssignment}
              value={selectedOrganizationalUnitId?.toString() ?? ""}
              onChange={(event) => setSelectedOrganizationalUnitId(Number(event.target.value) || null)}
            >
              {organizationalUnits
                .filter((organizationalUnit) => organizationalUnit.active || organizationalUnit.id === selectedOrganizationalUnitId)
                .map((organizationalUnit) => (
                <MenuItem key={organizationalUnit.id} value={organizationalUnit.id.toString()}>
                  {organizationalUnit.name}{organizationalUnit.active ? "" : " (retired)"}
                </MenuItem>
              ))}
            </LabeledSelect>
          </DialogContent>
          <DialogActions>
            <Button disabled={savingEpisodeAssignment} onClick={() => setPendingEpisodeAssignment(null)}>Cancel</Button>
            <Button
              variant="contained"
              disabled={savingEpisodeAssignment || (pendingEpisodeAssignment.episodeId == null && selectedOrganizationalUnitId == null)}
              loading={savingEpisodeAssignment}
              onClick={async () => {
                try {
                  if (pendingEpisodeAssignment.episodeId == null) {
                    const created = await createDefaultEpisode(
                      pendingEpisodeAssignment.serviceLineId,
                      selectedOrganizationalUnitId,
                    );
                    if (!created) { return; }
                  } else {
                    await updateEpisode({
                      variables: {
                        episodeId: pendingEpisodeAssignment.episodeId,
                        organizationalUnitId: selectedOrganizationalUnitId,
                        active: null,
                        autoschedulerGroup: null,
                        careRequirements: null,
                        precautionIds: null,
                      },
                    });
                  }
                  setPendingEpisodeAssignment(null);
                } catch (error) {
                  console.error(error);
                  setErrorMessage("Couldn't update the organizational unit. Please try again.");
                }
              }}
            >
              {pendingEpisodeAssignment.episodeId == null ? "Create episode" : "Save"}
            </Button>
          </DialogActions>
        </Dialog>
      )}
      {managingPrecautionsFor && (
        <EpisodePrecautionsDialog
          key={managingPrecautionsFor.episodeId}
          episodeId={managingPrecautionsFor.episodeId}
          episodeLabel={serviceLines[managingPrecautionsFor.serviceLineId]?.name || ""}
          currentPrecautionIds={
            episodes.find((ep) => ep.id === managingPrecautionsFor.episodeId)
              ?.precautions?.map((p) => p.precautionId) || []
          }
          onClose={() => setManagingPrecautionsFor(null)}
        />
      )}
    </Card>
  );
}

gql`
  query EpisodeOrganizationalUnits @permission(requires: managePatients) {
    organizationalUnit(orderBy: { name: { direction: asc, priority: 1 } }) {
      id
      name
      active
    }
  }
`;

const bull = (
  <Box
    component="span"
    sx={{
      display: "inline-block",
      mx: "10px",
      transform: "scale(1.5) translateY(-1px)",
    }}
  >
    |
  </Box>
);
